/**
 * Animated Reveal Text Component
 * Компонент для анимированного раскрытия текста с подсветкой
 * Адаптировано из shadcn/ui компонента
 */

import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils.js";

/**
 * @typedef {Object} HighlightTextProps
 * @property {string} text - Текст для отображения
 * @property {string} [as="h1"] - HTML тег для рендеринга (h1, h2, h3, h4, h5, h6, p, span)
 * @property {string} [className] - Дополнительные классы для контейнера
 * @property {string} [textClassName] - Дополнительные классы для текста
 * @property {string} [highlightClassName] - Дополнительные классы для подсветки
 * @property {number} [duration=1.5] - Длительность анимации в секундах
 * @property {string} [highlightColor="#d7ff7b"] - Цвет подсветки
 * @property {string} [ease="easeInOut"] - Тип анимации (easeIn, easeOut, easeInOut, linear)
 */

/**
 * Компонент для анимированного раскрытия текста с подсветкой
 */
const HighlightText = React.forwardRef(({
  text,
  as: Component = "h1",
  className,
  textClassName,
  highlightClassName,
  duration = 1.5,
  highlightColor = "#22c55e", // Зеленый цвет в стиле Vertex
  ease = "easeInOut",
  ...props
}, ref) => {
  const easeMap = {
    easeIn: [0.42, 0, 1, 1],
    easeOut: [0, 0, 0.58, 1],
    easeInOut: [0.42, 0, 0.58, 1],
    linear: [0, 0, 1, 1]
  };

  return (
    <div 
      ref={ref}
      className={cn("text-center", className)} 
      {...props}
    >
      <Component 
        className={cn(
          "text-3xl md:text-4xl lg:text-5xl font-[700] leading-[1.5] tracking-tight max-w-3xl",
          textClassName
        )}
      >
        <motion.span
          className={cn(
            "relative py-[10px] px-[8px] rounded-[10px] mb-[5px] mx-2 inline-block",
            "text-white font-bold",
            highlightClassName
          )}
          initial={{
            clipPath: "inset(0 100% 0 0)",
            backgroundColor: highlightColor
          }}
          animate={{
            clipPath: "inset(0 0% 0 0)",
          }}
          transition={{
            duration,
            ease: easeMap[ease] || easeMap.easeInOut
          }}
        >
          {text}
        </motion.span>
      </Component>
    </div>
  );
});

HighlightText.displayName = "HighlightText";

export { HighlightText };

